
import { Badge } from "@/components/ui/badge";
import { MapPin, BarChart3, Package, Truck, Activity, DollarSign, FileText } from "lucide-react";

interface UsdaReport {
  id: string;
  report_id: string;
  slug_id: string | null;
  slug_name: string | null;
  report_name: string | null;
  published_date: string | null;
  market_types: any;
  commodities: string[] | null;
  varieties: string[] | null;
  market_cities: string[] | null;
  report_type: string | null;
  created_at: string;
  updated_at: string;
}

interface ProfileReport {
  id: string;
  profile_id: string;
  report_id: string;
  market_city: string;
  relevance_score: number;
  report_name: string | null;
  market_types: any;
  report_type: string | null;
  created_at: string;
  updated_at: string;
  usda_reports: UsdaReport;
}

interface ReportCardProps {
  report: ProfileReport | UsdaReport;
  isProfileReport?: boolean;
}

export const ReportCard = ({ report, isProfileReport = false }: ReportCardProps) => {
  const getReportTypeIcon = (reportType: string | null) => {
    switch (reportType) {
      case 'terminal market prices':
        return <BarChart3 size={14} />;
      case 'wholesale market':
        return <Package size={14} />;
      case 'shipping point prices':
        return <Truck size={14} />;
      case 'daily movement':
        return <Activity size={14} />;
      case 'prices':
        return <DollarSign size={14} />;
      default:
        return <FileText size={14} />;
    }
  };

  const getReportTypeColor = (reportType: string | null) => {
    switch (reportType) {
      case 'terminal market prices':
        return 'bg-blue-100 text-blue-800';
      case 'wholesale market':
        return 'bg-green-100 text-green-800';
      case 'shipping point prices':
        return 'bg-purple-100 text-purple-800';
      case 'daily movement':
        return 'bg-orange-100 text-orange-800';
      case 'prices':
        return 'bg-yellow-100 text-yellow-800';
      case 'trends':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString();
  };

  const profileReport = isProfileReport ? report as ProfileReport : null;
  const usdaReport = isProfileReport ? (report as ProfileReport).usda_reports : report as UsdaReport;

  const reportType = profileReport?.report_type || usdaReport.report_type;
  const marketTypes = profileReport?.market_types || usdaReport.market_types;

  return (
    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-gray-900">
          {profileReport?.report_name || usdaReport.report_name || 'USDA Market Report'}
        </h4>
        <div className="flex items-center space-x-2">
          {reportType && (
            <Badge 
              className={`text-xs flex items-center space-x-1 ${getReportTypeColor(reportType)}`}
              variant="outline"
            >
              {getReportTypeIcon(reportType)}
              <span className="capitalize">{reportType}</span>
            </Badge>
          )}
          {isProfileReport && (
            <Badge 
              className="ml-2 bg-green-100 text-green-800 border-green-200"
              variant="outline"
            >
              Regional Match
            </Badge>
          )}
        </div>
      </div>
      
      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
        {profileReport && (
          <span className="flex items-center space-x-1">
            <MapPin size={14} />
            <span>{profileReport.market_city}</span>
          </span>
        )}
        <span>
          Published: {formatDate(usdaReport.published_date)}
        </span>
      </div>

      {marketTypes && (
        <div className="mb-2">
          <span className="text-sm font-medium text-gray-700">Market Types: </span>
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.keys(marketTypes).slice(0, 3).map((marketType, index) => (
              <Badge key={index} variant="outline" className="text-xs bg-purple-100 text-purple-800">
                {marketType}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {usdaReport.commodities && (
        <div className="mb-2">
          <span className="text-sm font-medium text-gray-700">Commodities: </span>
          <div className="flex flex-wrap gap-1 mt-1">
            {usdaReport.commodities.slice(0, 5).map((commodity, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {commodity}
              </Badge>
            ))}
            {usdaReport.commodities.length > 5 && (
              <Badge variant="secondary" className="text-xs">
                +{usdaReport.commodities.length - 5} more
              </Badge>
            )}
          </div>
        </div>
      )}

      {usdaReport.varieties && usdaReport.varieties.length > 0 && (
        <div>
          <span className="text-sm font-medium text-gray-700">Varieties: </span>
          <div className="flex flex-wrap gap-1 mt-1">
            {usdaReport.varieties.slice(0, 3).map((variety, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {variety}
              </Badge>
            ))}
            {usdaReport.varieties.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{usdaReport.varieties.length - 3} more
              </Badge>
            )}
          </div>
        </div>
      )}

      {isProfileReport && (
        <div className="mt-2 pt-2 border-t text-xs text-gray-500">
          <p>Report Type: {reportType || 'Not specified'}</p>
          <p>Market Types: {marketTypes ? 'Available' : 'None'}</p>
        </div>
      )}
    </div>
  );
};
